import { router, usePage } from '@inertiajs/vue3';
import { commonStore } from '../../../stores/common.js';
import { browserWebcamStreamingStore } from '../../../stores/browserWebcamStreaming';
import Swal from 'sweetalert2';

const wsTimer = 30000;

// Hold off on redirecting to /prova/liberacao-prova for this long after
// a WEBCAM-OFFLINE notification arrives. Browser_webrtc publishers may
// briefly drop and recover within ~2-3s on a flaky network; the
// recovered state lands as WEBCAM-ONLINE and cancels the pending
// redirect, sparing the student a round-trip through the unlock page.
// Only applies in browser_webrtc mode — agent_rtsps disconnects are
// coarse signals that should still redirect immediately.
const webcamOfflineRedirectDebounceMs = 5000;

// Pages reached only after the exam result is registered. Once the
// student is here, the platform itself tears the webcam/screen streams
// down as part of finishing the exam, so OFFLINE notifications are
// expected and must not bounce the student to the unlock page — doing
// so used to hit check-blocks with a no-longer-OPEN test and log the
// student out mid-signature.
const postExamPaths = [
	'/prova/digital-signature',
	'/prova/v3/signature',
	'/prova/feedback',
];

export default class Websocket_exam {
	websocket_url: string;
	ws: WebSocket | undefined = undefined;
	common;
	browserWebcam;
	webcamOfflineRedirectTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(websocket_url: string) {
		this.websocket_url = websocket_url;
		this.common = commonStore();
		this.browserWebcam = browserWebcamStreamingStore();
	}

	connectWs() {
		this.ws = new WebSocket(this.websocket_url);
		this.ws.onmessage = (message) => {
			this.receiveMessage(message);
		};

		this.ws.onclose = (message) => {
			// console.log('onClose', this.websocket_url, message)
			// this.connectWs()
		};

		this.ws.onerror = (message) => {
			// console.log('onError', this.websocket_url, message)
			// this.connectWs()
		};

		this.ws.onopen = (message) => {
			// console.log('onOpen', message)
		};
	}

	getWsStatus() {
		const status = {
			0: 'Connecting',
			1: 'Open',
			2: 'Closing',
			3: 'Closed',
		};
		if (this.ws?.readyState === undefined) {
			return status[2];
		}
		return status[this.ws.readyState];
	}

	processMessage(data) {
		switch (data['event']) {
			case 'cancel/exam-schedule':
				switch (data['subevent']) {
					case 'goto_page': {
						setTimeout(() => {
							this.browserWebcam.stop('exam-canceled');
							window.location.href = data['data'].logout_url;
						}, wsTimer);

						Swal.fire({
							title: 'Sua prova foi cancelada',
							text: 'Procure o atendimento para receber instruções. Você será redirecionado em 30 segundos.',
							icon: 'warning',
							showConfirmButton: false,
							allowOutsideClick: false,
							timerProgressBar: true,
							timer: wsTimer,
						});
						break;
					}

					default: {
						break;
					}
				}

				break;
			case 'monitoria/redirect':
				switch (data['subevent']) {
					case 'goto_page': {
						setTimeout(() => {
							this.browserWebcam.stop('exam-redirected');
							window.location.href = data['data'].logout_url;
						}, wsTimer);

						Swal.fire({
							title: 'Sua prova continuará em outro computador',
							text: 'Procure o atendimento para receber instruções. Você será redirecionado em 30 segundos.',
							icon: 'warning',
							showConfirmButton: false,
							allowOutsideClick: false,
							timerProgressBar: true,
							timer: wsTimer,
						});
						break;
					}

					default: {
						break;
					}
				}

			case 'webcam/start':
				console.log('webcam/start');
				break;

			case 'webcam/stop':
				console.log('webcam/stop');
				break;

			case 'webcam/restart':
				console.log('webcam/restart');
				break;

			case 'NOTIFICATIONS/NEW':
				switch (data['subevent']) {
					case 'CAMERA-CHANNEL-OFFLINE':
						var verifyComputerInList =
							data.data.list_computer_to_action.indexOf(
								this.common.get_computer_id
							);
						if (verifyComputerInList > 0) {
							this.redirectToUnlockPage();
							this.common.list_camera_dvr =
								data.data.list_camera_channel;
						}
						break;
					case 'CAMERA-CHANNEL-ONLINE':
						this.common.list_camera_dvr =
							data.data.list_camera_channel;
						break;
					case 'WEBCAM-OFFLINE':
						// Browser_webrtc publishers flap on flaky networks; debounce
						// so a transient drop+recover cycle doesn't bounce the student
						// out of the exam. Agent mode = immediate redirect (legacy).
						if (this.isBrowserWebcamMode()) {
							this.scheduleWebcamOfflineRedirect();
						} else {
							this.redirectToUnlockPage();
						}
						break;
					case 'SCREEN-OFFLINE':
						// Screen is always published via the Windows agent (no
						// browser-side equivalent), so its disconnects are not
						// subject to WHIP flap. Keep immediate behavior.
						this.redirectToUnlockPage();
						break;
					case 'WEBCAM-ONLINE':
						// Cancel any pending webcam-offline redirect (the publisher
						// recovered within the debounce window).
						this.cancelWebcamOfflineRedirect();
						break;
					case 'SCREEN-ONLINE':
						// Stream restored. The unlock page polls server-side
						// state and lets the student resume; nothing extra
						// to do here.
						break;
					// DEGRADED events are dispatched to admin role only — the
					// student-role broadcast is suppressed at the handler.
					// Ignore defensively in case one ever leaks through.
					case 'WEBCAM-DEGRADED':
					case 'SCREEN-DEGRADED':
					case 'CAMERA-CHANNEL-DEGRADED':
						break;

					default:
						break;
				}
				break;

			default:
				break;
		}
	}

	receiveMessage(message) {
		if (message['data'] instanceof Blob) {
			message['data'].text().then((e) => {
				this.processMessage(JSON.parse(e));
			});
		} else {
			const data = JSON.parse(message['data']);
			this.processMessage(data);
		}
	}

	isBrowserWebcamMode(): boolean {
		const props = usePage().props as Record<string, unknown> | undefined;
		return Boolean(props?.browser_webcam_streaming_enabled);
	}

	isOnPostExamPage(): boolean {
		const path = window.location.pathname;
		return postExamPaths.some((postExamPath) =>
			path.startsWith(postExamPath)
		);
	}

	redirectToUnlockPage() {
		if (this.isOnPostExamPage()) {
			return;
		}
		router.get('/prova/liberacao-prova');
	}

	scheduleWebcamOfflineRedirect() {
		if (this.webcamOfflineRedirectTimer !== null) {
			return;
		}
		this.webcamOfflineRedirectTimer = setTimeout(() => {
			this.webcamOfflineRedirectTimer = null;
			// Re-check at fire time: the student may have advanced to the
			// signature/feedback pages during the debounce window.
			this.redirectToUnlockPage();
		}, webcamOfflineRedirectDebounceMs);
	}

	cancelWebcamOfflineRedirect() {
		if (this.webcamOfflineRedirectTimer !== null) {
			clearTimeout(this.webcamOfflineRedirectTimer);
			this.webcamOfflineRedirectTimer = null;
		}
	}
}
